import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Typography,
  Button,
  Box,
  Card,
} from '@mui/material';
import { printPDF } from '../../../../fetch/fileFetch';
import {
  RowData,
  MessageHistoryProps,
} from '../../../../types/summarizeTable';
import theme from '../../../../theme/theme';
import OptimizationTable from './OptimizationTable';
import MessagesForTopic from './MessagesForTopic';
const MessageHistory: React.FC<MessageHistoryProps> = ({ messageHistory, onSelect }) => {
  const { t } = useTranslation('optimizer');
  return (
    <Box>
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          fontSize: '24px',
          color: '#1A202C',
          mb: 4,
          mt: 4,
          textAlign: 'center',
        }}
      >
        {t('summarize.enableChatHistory')}
      </Typography>
      <MessagesForTopic
        topics={Object.keys(messageHistory)}
        onSelectAll={(selected) => onSelect('all', selected)}
        onSelectTopic={(topic, selected) => onSelect(topic, selected)}
      />
    </Box>
  );
};
const SummarizeTable: React.FC = () => {
  const { t } = useTranslation('optimizer');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const selectedOptions: RowData[] = JSON.parse(localStorage.getItem('selectedOptions') || '[]');
  const messageHistory = JSON.parse(localStorage.getItem('MessageHistory') || '[]');
  const priceSummary = JSON.parse(localStorage.getItem('priceSummary') || '');
  const { percentageSaved, prevTotal, saving, total } = priceSummary;
  useEffect(() => {
    if (selectedOptions.length === 0 && messageHistory.length === 0) {
      navigate('/optimizer-list');
    } else {
      setLoading(false);
    }
  }, [selectedOptions, messageHistory, navigate]);
  if (loading) {
    return <div></div>;
  }
  const handleTopicSelect = (topic: string, isSelected: boolean) => {
    setSelectedTopics((prev) =>
      isSelected ? [...prev, topic] : prev.filter((t) => t !== topic),
    );
  };
  const handlePrintClick = async () => {
    try {
      const filteredMessages = Object.entries(messageHistory).filter(([topic]) =>
        selectedTopics.includes(topic),
      );
      const filteredHistory = Object.fromEntries(filteredMessages);
      await printPDF(selectedOptions, priceSummary, filteredHistory);
    } catch (error) {
      console.error(t('summarize.pdfGenerationError'), error);
    }
  };
  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h4"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          my: 4,
          color: '#1A202C',
          fontSize: '24px',
          fontWeight: 600,
        }}
      >
        {t('summarize.title')}
      </Typography>
      {selectedOptions.length > 0 && (
        <>
          <OptimizationTable selectedOptions={selectedOptions} />
          <Card sx={{ mt: 4, p: 2, boxShadow: 'none', border: '2px solid #e6e8eb', borderRadius: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 500 }}><strong>{t('table.total')}</strong></Typography>
              {!!saving && (
                <Typography
                  variant="h6"
                  color="success.main"
                  sx={{ textDecoration: 'line-through' }}
                >
                  {prevTotal.toFixed(2)}$
                </Typography>
              )}
              <Typography variant="h6" color="success.main" sx={{ fontWeight: 500 }}>
                {total.toFixed(2)}$
              </Typography>
            </Box>
            {!!saving && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 500 }}>
                  <strong>{t('table.savings')}:</strong>
                </Typography>
                <Typography variant="h6" color="success.main" sx={{ fontWeight: 500 }}>
                  +{saving.toFixed(2)}$ (+{percentageSaved}%)
                </Typography>
              </Box>
            )}
          </Card>
        </>
      )}
      {Object.keys(messageHistory).length > 0 && (
        <MessageHistory
          messageHistory={messageHistory}
          onSelect={handleTopicSelect}
        />
      )}
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handlePrintClick}
          sx={{
            bgcolor: theme.palette.primary.main,
            color: 'white',
            p: '0.5rem 2rem',
            fontSize: '1.1rem',
            textTransform: 'none',
            transition: 'all 0.2s ease',
            '&:hover': {
              bgcolor: '#4A6FE8',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
            },
            '&.Mui-disabled': {
              bgcolor: '#E2E8F0',
              color: '#A0AEC0',
            },
          }}
        >
          {t('summarize.print')}
        </Button>
      </Box>
    </Box>
  );
};
export default SummarizeTable;
